import React from 'react';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { HeidiSessionStatus, useHeidiSession } from '@components/Clinicians/Session/hooks/useHeidiSession';
import { WarningOutlined } from '@ant-design/icons';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const IconButton = styled.button<{ status: HeidiSessionStatus }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  border: 1px solid #868686;

  background-color: ${({ status }) => {
    if (status === 'active') return '#f97316';
    return '#d1d5db';
  }};

  ${({ status }) =>
    status === 'active' &&
    css`
      animation: ${pulse} 2s ease-in-out infinite;
    `}
  &:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }

  img {
    width: 22px;
    height: 22px;
    filter: brightness(0) invert(1);
  }

  .anticon {
    font-size: 22px;
  }
`;

const statusTitleMap: Record<HeidiSessionStatus, string> = {
  idle: 'AI Off',
  active: 'AI Transcribing',
  paused: 'AI Paused',
  processing: 'Generating Notes',
  completed: 'Review Mode'
};

const HeidiAiIcon = ({ expandPanel }: { expandPanel: () => void }) => {
  const { t } = useTranslation('common');
  const { status, hasHeidiError } = useHeidiSession();

  return (
    <IconButton
      onClick={() => expandPanel()}
      status={status}
      title={t(statusTitleMap[status])}
      aria-label={t(statusTitleMap[status])}
      data-testid="heidi-ai-icon"
    >
      {hasHeidiError ? <WarningOutlined /> : <img src="/static/svg/ai-button.svg" alt={t('AI Assistant')} />}
    </IconButton>
  );
};

export default HeidiAiIcon;
